import type { ReactElement, ReactNode } from 'react';
import { forwardRef, memo } from 'react';
import { isNaN } from 'lodash';
import styled, { css } from 'styled-components';
import type { Resource } from '@frameio/graphql-client/generated';
import { useGetDirectMembershipsForFacepileQuery } from '@frameio/graphql-client/generated';
import { useGetResourceQuery } from '@frameio/graphql-client/graphql/features/invites/queries/generated/GetResource';
import { useActiveAccountId } from '@frameio/shared/contexts';
import type {
  AvatarGroupPillProps,
  ThemeColorsBackground,
} from '@frameio/vapor';
import { AvatarGroup, AvatarGroupPill, theme } from '@frameio/vapor';
import type { IconProps } from '@frameio/vapor-icons';
import { IconPeopleFill } from '@frameio/vapor-icons';
import type { ExpandableView } from '../../hooks/useMemberAccessFlow';
import { InviteButtonAvatar } from '../InviteButtonAvatar';
import TEXT from './generated/TEXT/MemberAccessDialogButton';

TEXT.DEFINE({
  /** Button text for the member access dialog button that adds members to a resource */
  buttonLabel: 'Add',
  /** @notranslate */
  nbsp: '\u00A0',
});

export type CustomMemberAccessDialogButtonProps = {
  cool?: boolean;
  onClick?: (view?: ExpandableView) => void;
  onHover: () => void;
  bind: any;
};

export type MemberAccessDialogButtonProps = {
  children?: (CustomMemberAccessDialogButtonProps) => ReactNode;
  ctaVariant?: AvatarGroupPillProps['variant'];
  label?: string;
  onClick: () => void;
  onHover: (boolean) => void;
  renderButton?: (CustomMemberAccessDialogButtonProps) => ReactNode;
  resourceId?: Resource['id'];
  avatarIcon?: ReactElement<IconProps>;
};

// This logic is used both in the Invite Button with the icon
// and in the count in the invite modal
const determineDisplayCount = (totalCount: number, avatarIcon: ReactNode) => {
  // If we have a count greater than 0 or an avatar icon, determine the display value
  if (totalCount > 0 || avatarIcon) {
    if (totalCount > 999) {
      return `${Math.floor(totalCount / 1000)}K`;
    }
    return totalCount;
  }
  // If count is zero and there's no avatar icon, hide the count
  return null;
};

const MemberAccessDialogButtonComponent = forwardRef<
  HTMLDivElement,
  MemberAccessDialogButtonProps
>(
  (
    {
      children,
      ctaVariant = 'primary',
      label = TEXT.buttonLabel,
      onClick,
      onHover,
      resourceId,
      avatarIcon,
    },
    forwardedRef
  ) => {
    const accountId = useActiveAccountId();
    const { data } = useGetDirectMembershipsForFacepileQuery({
      variables: { accountId: accountId ?? '', resourceId },
      skip: !resourceId || !accountId,
      fetchPolicy: 'cache-first',
    });

    let isRestrictedProject = false;

    const { data: resourceData } = useGetResourceQuery({
      variables: { accountId: accountId ?? '', id: resourceId },
      skip: !accountId,
      fetchPolicy: 'cache-first',
    });

    if (resourceData?.account?.resource?.__typename === 'Project') {
      isRestrictedProject = resourceData.account.resource.private;
    }

    const directMemberships = data?.account?.resource?.directMemberships;
    const directMembershipCount = directMemberships?.totalCount ?? 0;

    // For non-restricted projects,
    // that needs unique, non admin members count
    const uniqueMemberCount = data?.account?.resource?.uniqueMemberCount ?? 0;

    const displayDirectMembershipOnly =
      isRestrictedProject ||
      resourceData?.account?.resource?.__typename === 'Workspace';

    const totalCount = displayDirectMembershipOnly
      ? directMembershipCount
      : uniqueMemberCount;

    /**
     * We want to check if totalCount exists and is a number rather than whether
     * it's true or false, because 0 is a valid value we want to display.
     */
    const isVisible = !isNaN(totalCount);

    if (typeof children === 'function') {
      return (
        <StyledDiv $isVisible={isVisible}>
          {children({
            memberCount: totalCount ?? TEXT.nbsp,
            onClick,
            onHover,
          })}
        </StyledDiv>
      );
    }

    return (
      <StyledDiv $isVisible={isVisible}>
        <AvatarGroup
          ref={forwardedRef}
          aria-label={label}
          onHoverStart={onHover}
          onPress={() => onClick()}
        >
          {/**
           * Always slice down to 3 in the event that more memberships have been
           * added to the cache since the initial query, to prevent more than 3
           * from displaying
           */
          directMemberships?.nodes.slice(0, 3).map((membership) => {
            const { accountUser, accountUserGroup } = membership;

            if (accountUserGroup) {
              return (
                <InviteButtonAvatar
                  key={membership.id}
                  background="secondary"
                  emoji={accountUserGroup.emoji}
                  title={accountUserGroup.name}
                />
              );
            }
            if (accountUser) {
              return (
                <InviteButtonAvatar
                  key={membership.id}
                  background={accountUser.avatarColor as ThemeColorsBackground}
                  src={accountUser.profileImageUrl}
                  title={accountUser?.displayName}
                />
              );
            }

            return null;
          })}
          <AvatarGroupPill
            icon={avatarIcon ?? <IconPeopleFill />}
            variant={ctaVariant}
          >
            {determineDisplayCount(totalCount, avatarIcon)}
          </AvatarGroupPill>
        </AvatarGroup>
      </StyledDiv>
    );
  }
);

MemberAccessDialogButtonComponent.displayName =
  'MemberAccessDialogButtonComponent';

export const MemberAccessDialogButton = memo(MemberAccessDialogButtonComponent);

MemberAccessDialogButton.displayName = 'MemberAccessDialogButton';

const buttonTransition = css`
  transition: opacity ${theme.transitions.durations[200]}
    ${theme.transitions.eases.primary};
`;

const StyledDiv = styled.div<{ $isVisible?: boolean }>`
  ${buttonTransition}
  opacity: ${(p) => (p.$isVisible ? 1 : 0)};
`;
